import React, { useEffect, useState } from "react";
import { getSf009AddedByDisplay } from "./sf009AddedByDisplay";
import { toast } from "react-toastify";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getRawSf009FormData } from "./sf009FormData";
import {
  formatSf009DateDisplay,
  formatSf009DateDisplayOrDash,
  parseSf009DateForPicker,
  datePickerToSf009DateString,
} from "./sf009ImportDateUtils";
import "./horizonTable.css";
import "../forms/formTitle.css";
import { applyFooterAllPages } from "../../utils/formPdfHelpers.js";
import onsiteproLogo from "../../assets/logo2 2.png";
import AdminTaylorImportModal, { adminIsPrimaryAdmin } from "./AdminTaylorImportModal";
import { parseSiteRegisterDate } from "./adminEditModalTheme";
import { ensureFormUuid } from "../../utils/formUuid";
import {
  buildSf009SharePointFileName,
  createSf009SharePointUploadConfig,
  getSf009SharePointFields,
  postSf009PdfToSharePoint,
} from "./sf009SharePointUpload";
import { drawSignatureImageInPdfCell, isSigImage, toSigDataUrl } from "./sf009FormSignatureUtils";

import { canEditDeleteRecords } from "../../utils/rolePermissions";
const THEME = "#8b1634";
const THEME_RGB = [139, 22, 52];
const THEME_LIGHT = "rgb(239, 226, 231)";
const THEME_LIGHT_RGB = [239, 226, 231];
const THEME_BORDER = "rgba(139, 22, 52, 0.18)";
const PDF_WHITE = [255, 255, 255];
const PDF_PAGE_BOTTOM = 32;
const LOG_INFO_LEGEND_LINES = [
  "Information to be recorded in log:",
  "(1) Pre-energising IP2X checks on electrical equipment",
  "(2) Initial energising of electrical apparatus;",
  "(3) Safety Isolations & testing of electrical systems;",
  "(4) Faults or significant defects",
  "(5) Modifications to equipment",
];

function normalizeLogSignature(val) {
  if (!isSigImage(val)) return "";
  const s = String(val).trim();
  return s.startsWith("data:image") ? s : `data:image/png;base64,${s}`;
}

const DEFAULT_FORM_DATA = {
  projectNo: "",
  projectName: "",
  siteLocation: "",
  logEntries: [],
};

function getBaseUrl() {
  const apiUrl = process.env.REACT_APP_API_URL;
  return apiUrl?.endsWith("/") ? apiUrl.slice(0, -1) : apiUrl || "";
}

function normalizeLogRow(src) {
  const row = src && typeof src === "object" ? src : {};
  return {
    dateTime: row.dateTime ?? row.date_time ?? row.dateAndTime ?? "",
    description:
      row.description ??
      row.descriptionOfOperation ??
      row.description_of_operation_or_event ??
      "",
    precautionsControls:
      row.precautionsControls ??
      row.precautions_and_controls ??
      row.precautions ??
      "",
    printedName: row.printedName ?? row.printed_name ?? "",
    signature: normalizeLogSignature(row.signature),
  };
}

function logRowSigKey(row) {
  return [row.dateTime, row.printedName]
    .map((v) => String(v ?? "").trim().toLowerCase())
    .join("||");
}

function buildLogSignatureLock(entries) {
  const map = new Map();
  (entries || []).forEach((row) => {
    const key = logRowSigKey(row);
    if (!key || key === "||||") return;
    map.set(key, { signature: normalizeLogSignature(row.signature) });
  });
  return map;
}

function applyLockedLogSignatures(entries, lockMap) {
  if (!lockMap || lockMap.size === 0) return entries;
  return entries.map((row) => {
    const locked = lockMap.get(logRowSigKey(row));
    if (!locked) return row;
    return { ...row, signature: locked.signature || row.signature };
  });
}

function hasLogRowData(row) {
  if (!row || typeof row !== "object") return false;
  return [row.dateTime, row.description, row.precautionsControls, row.printedName, row.signature].some(
    (v) => v != null && String(v).trim() !== ""
  );
}

function logEntriesFromRaw(entries) {
  const list = Array.isArray(entries) ? entries.map(normalizeLogRow) : [];
  return list.filter(hasLogRowData);
}

function cloneLogEntries(entries) {
  return Array.isArray(entries) ? entries.map(normalizeLogRow) : [];
}

function entriesForEditDisplay(entries) {
  return cloneLogEntries(entries);
}

function mergeRecordIntoDefaults(raw) {
  const src = raw && typeof raw === "object" ? raw : {};
  const hd = src.headerDetails || {};
  const entries = src.logEntries ?? src.entries ?? src.safetyLogEntries;
  return {
    projectNo: src.projectNo ?? hd.projectNo ?? "",
    projectName: src.projectName ?? hd.projectName ?? "",
    siteLocation: src.siteLocation ?? hd.siteLocation ?? src.site ?? "",
    logEntries: logEntriesFromRaw(entries),
  };
}

const sf009TblBorder = `1px solid ${THEME_BORDER}`;
const sf009LblCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 12,
  fontWeight: 600,
  background: THEME,
  color: "#fff",
  verticalAlign: "middle",
  whiteSpace: "nowrap",
};
const sf009HdrLblWrap = {
  ...sf009LblCell,
  whiteSpace: "normal",
  lineHeight: 1.35,
  fontSize: 11,
  verticalAlign: "middle",
};
const sf009ValCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 12,
  background: "#fff",
  verticalAlign: "middle",
};
const sf009RegHeadCell = {
  border: sf009TblBorder,
  padding: "6px 8px",
  fontSize: 10,
  fontWeight: 600,
  background: THEME_LIGHT,
  color: THEME,
  textAlign: "center",
  verticalAlign: "middle",
};
const sf009RegCell = {
  border: sf009TblBorder,
  padding: "4px 6px",
  fontSize: 11,
  background: "#fff",
  verticalAlign: "top",
};
const sf009CellInput = {
  width: "100%",
  border: "none",
  outline: "none",
  fontSize: 11,
  padding: "4px 2px",
  background: "transparent",
  boxSizing: "border-box",
};
const sf009SigCell = {
  ...sf009RegCell,
  textAlign: "center",
  verticalAlign: "middle",
  padding: "4px",
  minWidth: 90,
};
const sf009LegendCell = {
  border: sf009TblBorder,
  padding: "8px 10px",
  fontSize: 10,
  background: "#fff",
  verticalAlign: "top",
  lineHeight: 1.45,
};

function LogSignatureDisplay({ value }) {
  const url = normalizeLogSignature(value);
  if (!url) {
    return <span style={{ color: "#a0aec0", fontSize: 10 }}>—</span>;
  }
  return (
    <img
      src={url}
      alt="Signature"
      style={{
        maxWidth: "100%",
        maxHeight: 52,
        objectFit: "contain",
        display: "block",
        margin: "0 auto",
      }}
      onError={(e) => {
        e.target.style.display = "none";
      }}
    />
  );
}

function Sf009ISwitchRoomElectricalSafetyLogPage() {
  const canEditDelete = canEditDeleteRecords();
  const [forms, setForms] = useState([]);
  const [loadingRecords, setLoadingRecords] = useState(true);
  const [appUsers, setAppUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const pageSize = 10;
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showImportModal, setShowImportModal] = useState(false);
  const isAdminUser = adminIsPrimaryAdmin();
  const [editForm, setEditForm] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [uploadingKey, setUploadingKey] = useState(null);
  const [logSignatureLock, setLogSignatureLock] = useState(null);

  useEffect(() => {
    fetchForms();
    fetchAppUsers();
  }, []);

  const getRawFormData = getRawSf009FormData;

  async function fetchForms() {
    setLoadingRecords(true);
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-i-switch-room-electrical-safety-log", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setForms(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setForms([]);
    } finally {
      setLoadingRecords(false);
    }
  }

  async function fetchAppUsers() {
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/app-users", {
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      setAppUsers(res.ok && data.success && Array.isArray(data.data) ? data.data : []);
    } catch {
      setAppUsers([]);
    }
  }

  const filtered = forms.filter((f) => {
    const fd = mergeRecordIntoDefaults(getRawFormData(f));
    const fields = [fd.projectNo, fd.projectName, fd.siteLocation, f.name].map((v) => (v || "").toLowerCase());
    const words = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    return words.length === 0 || words.every((w) => fields.some((field) => field.includes(w)));
  });
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const paged = filtered.slice((page - 1) * pageSize, page * pageSize);

  const updateFormData = (section, key, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      if (section === "_flat") {
        next.formData[key] = value;
      } else if (section && key) {
        next.formData[section] = { ...(next.formData[section] || {}), [key]: value };
      }
      const fd = next.formData;
      next.name = fd.projectName || "";
      return next;
    });
  };

  const updateLogRow = (rowIndex, field, value) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneLogEntries(next.formData.logEntries);
      entries[rowIndex] = { ...entries[rowIndex], [field]: value };
      next.formData.logEntries = entries;
      next.name = next.formData.projectName || "";
      return next;
    });
  };

  const removeLogRow = (rowIndex) => {
    setEditForm((p) => {
      const next = { ...p, formData: { ...p.formData } };
      const entries = cloneLogEntries(next.formData.logEntries);
      entries.splice(rowIndex, 1);
      next.formData.logEntries = entries;
      return next;
    });
  };

  const handleEdit = (f) => {
    const raw = getRawFormData(f);
    const merged = mergeRecordIntoDefaults(raw);
    setLogSignatureLock(buildLogSignatureLock(merged.logEntries));
    setEditForm({
      ...f,
      formUuid: ensureFormUuid(f?.formUuid),
      formData: merged,
      name: merged.projectName || f.name || "",
    });
    setShowEditModal(true);
  };

  const handleEditSave = async () => {
    if (!editForm?._id) return;
    try {
      const token = localStorage.getItem("token");
      const raw = editForm.formData || {};
      const entriesToSave = applyLockedLogSignatures(logEntriesFromRaw(raw.logEntries), logSignatureLock);
      const firstDt = entriesToSave[0]?.dateTime;
      const res = await fetch(getBaseUrl() + "/api/sf-009-i-switch-room-electrical-safety-log/" + editForm._id, {
        method: "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({
          ...raw,
          logEntries: entriesToSave,
          name: editForm.name || raw.projectName,
          date: firstDt || editForm.date,
          formUuid: ensureFormUuid(editForm.formUuid),
        }),
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        setShowEditModal(false);
        setEditForm(null);
        setLogSignatureLock(null);
        toast.success("Form updated!");
      } else {
        toast.error(data.message || "Failed to update");
      }
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = (id) => {
    setDeleteId(id);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteId) return;
    try {
      const token = localStorage.getItem("token");
      const res = await fetch(getBaseUrl() + "/api/sf-009-i-switch-room-electrical-safety-log/" + deleteId, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      const data = await res.json();
      if (res.ok && data.success) {
        await fetchForms();
        toast.success("Form deleted");
      } else {
        toast.error(data.message || "Failed to delete");
      }
    } catch {
      toast.error("Failed to delete");
    } finally {
      setShowDeleteModal(false);
      setDeleteId(null);
    }
  };

  const formatDate = (d) => {
    if (!d) return "—";
    const parsed = parseSf009DateForPicker(d);
    if (parsed) return parsed.toLocaleDateString("en-GB");
    try {
      return new Date(d).toLocaleDateString("en-GB");
    } catch {
      return String(d);
    }
  };

  const getUserName = (record) => getSf009AddedByDisplay(record, appUsers);

  const fd = editForm?.formData || {};

  const generatePDF = async (record, options = {}) => {
    const shouldUploadToSharePoint = options.uploadOnly === true;
    const uploadConfig = shouldUploadToSharePoint ? createSf009SharePointUploadConfig(options) : null;

    const recordRaw = getRawFormData(record);
    const raw = mergeRecordIntoDefaults(recordRaw);
    const entries = logEntriesFromRaw(recordRaw.logEntries);

    const doc = new jsPDF({ orientation: "landscape" });
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const contentX = 10;
    const contentW = pageWidth - 20;
    const pdfMargin = { left: contentX, right: contentX, bottom: PDF_PAGE_BOTTOM };
    const headerHeight = 30;

    doc.setFillColor(...THEME_RGB);
    doc.rect(0, 0, pageWidth, headerHeight, "F");
    try {
      const logoImg = new Image();
      await new Promise((resolve) => {
        logoImg.onload = resolve;
        logoImg.onerror = resolve;
        logoImg.src = onsiteproLogo;
      });
      doc.addImage(logoImg, "PNG", 10, 5, 65, 20);
    } catch {}

    doc.setTextColor(255, 255, 255);
    doc.setFont("helvetica", "bold");
    doc.setFontSize(16);
    doc.text("Switch Room Electrical Safety Log", 90, 13);
    doc.setFont("helvetica", "normal");
    doc.setFontSize(8);
    doc.text("TAYLOR TECHNOLOGY SYSTEMS LIMITED", 90, 19);

    let yPosition = headerHeight + 8;

    const pdfGrid = {
      font: "helvetica",
      fontSize: 7.5,
      cellPadding: 2.5,
      lineWidth: 0.2,
      textColor: [0, 0, 0],
      valign: "middle",
      overflow: "linebreak",
    };
    const pdfLbl = { fillColor: THEME_RGB, fontStyle: "bold", textColor: [255, 255, 255], fontSize: 7.5 };
    const pdfVal = { fillColor: PDF_WHITE, fontSize: 7.5 };
    const pdfRegHead = {
      fillColor: THEME_LIGHT_RGB,
      fontStyle: "bold",
      textColor: THEME_RGB,
      fontSize: 7,
      halign: "center",
      valign: "middle",
    };
    const pdfRegBody = { fillColor: PDF_WHITE, fontSize: 7, valign: "middle", minCellHeight: 10 };

    const logColW = {
      0: contentW * 0.12,
      1: contentW * 0.36,
      2: contentW * 0.26,
      3: contentW * 0.14,
      4: contentW * 0.12,
    };

    const ensureSpace = (needed = 20) => {
      if (yPosition + needed > pageHeight - PDF_PAGE_BOTTOM) {
        doc.addPage();
        yPosition = headerHeight + 8;
      }
    };

    const addPdfTable = (tableConfig, spaceNeeded = 20) => {
      ensureSpace(spaceNeeded);
      autoTable(doc, {
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        ...tableConfig,
        startY: yPosition,
      });
      yPosition = doc.lastAutoTable.finalY;
    };

    addPdfTable(
      {
        body: [
          [
            "Project No:",
            raw.projectNo || "",
            "Project Name:",
            raw.projectName || "",
            "Site / Location:",
            raw.siteLocation || "",
          ],
        ],
        columnStyles: {
          0: { cellWidth: contentW * 0.11, ...pdfLbl },
          1: { cellWidth: contentW * 0.22, ...pdfVal },
          2: { cellWidth: contentW * 0.11, ...pdfLbl },
          3: { cellWidth: contentW * 0.22, ...pdfVal },
          4: { cellWidth: contentW * 0.11, ...pdfLbl },
          5: { cellWidth: contentW * 0.23, ...pdfVal },
        },
        styles: pdfGrid,
      },
      18
    );

    const pdfLogSigCells = [];
    const logBody = entries.map((row, rowIdx) => {
      const sig = normalizeLogSignature(row.signature);
      if (sig) pdfLogSigCells.push({ row: rowIdx, col: 4, data: sig });
      return [
        row.dateTime || "",
        row.description || "",
        row.precautionsControls || "",
        row.printedName || "",
        sig ? " " : "",
      ];
    });

    if (logBody.length > 0) {
      ensureSpace(40);
      autoTable(doc, {
        startY: yPosition,
        theme: "grid",
        tableWidth: contentW,
        margin: pdfMargin,
        rowPageBreak: "auto",
        showHead: "firstPage",
        head: [
          [
            { content: "Date & Time", styles: pdfRegHead },
            {
              content: "Description of Operation or Event, Present Status & Safety Status",
              styles: pdfRegHead,
            },
            { content: "Precautions & Controls", styles: pdfRegHead },
            { content: "Printed Name", styles: pdfRegHead },
            { content: "Signature", styles: pdfRegHead },
          ],
        ],
        body: logBody,
        styles: pdfGrid,
        bodyStyles: pdfRegBody,
        headStyles: {
          fillColor: THEME_LIGHT_RGB,
          textColor: THEME_RGB,
          fontStyle: "bold",
          fontSize: 7,
          halign: "center",
          valign: "middle",
          lineWidth: 0.2,
        },
        columnStyles: {
          ...logColW,
          4: { ...logColW[4], minCellHeight: 20 },
        },
        didDrawCell: (data) => {
          if (data.section !== "body") return;
          const hit = pdfLogSigCells.find((s) => s.row === data.row.index && s.col === data.column.index);
          if (!hit) return;
          drawSignatureImageInPdfCell(doc, data, toSigDataUrl(hit.data), data.column.index);
        },
      });
      yPosition = doc.lastAutoTable.finalY;
    }

    addPdfTable(
      {
        body: [[{ content: LOG_INFO_LEGEND_LINES.join("\n"), colSpan: 5, styles: { fontSize: 7, valign: "top" } }]],
        columnStyles: { 0: { cellWidth: contentW } },
        styles: { ...pdfGrid, fillColor: PDF_WHITE },
      },
      36
    );

    applyFooterAllPages(doc);

    const displayName = raw.projectName || record.name || "SafetyLog";
    const safeSite = String(raw.projectName || raw.siteLocation || "Site").replace(/[\\/:"*?<>|]+/g, "-");
    const safeName = String(displayName).replace(/[\\/:"*?<>|]+/g, "-");
    const safeDateStr = String(formatDate(record.date || entries[0]?.dateTime) || "form").replace(/[\\/:"*?<>|]+/g, "-");
    const fileName =
      shouldUploadToSharePoint && uploadConfig
        ? buildSf009SharePointFileName("I", getSf009SharePointFields("I", raw, record, getUserName))
        : `SF009I_${safeName}_${safeDateStr}.pdf`;

    if (shouldUploadToSharePoint && uploadConfig) {
      try {
        const key = `${record._id || ""}|${raw.projectName || ""}|${record.date || ""}`;
        await postSf009PdfToSharePoint({ doc, fileName, uploadConfig, setUploadingKey, uploadKey: key });
        toast.success("PDF uploaded to SharePoint successfully!");
      } catch (err) {
        toast.error(err?.message || "SharePoint upload failed");
      }
    } else {
      doc.save(fileName);
      toast.success("SF-009-I Switch Room Electrical Safety Log PDF generated successfully!");
    }
  };

  const renderLogForm = () => {
    const entries = entriesForEditDisplay(fd.logEntries);
    return (
      <div style={{ border: sf009TblBorder, background: "#fff" }}>
        <div
          style={{
            textAlign: "center",
            padding: "14px 12px",
            fontWeight: 700,
            fontSize: 15,
            color: "#1a202c",
            borderBottom: sf009TblBorder,
          }}
        >
          Switch Room Electrical Safety Log
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", tableLayout: "fixed" }}>
          <tbody>
            <tr>
              <td style={{ ...sf009LblCell, width: "11%" }}>Project No:</td>
              <td style={{ ...sf009ValCell, width: "22%" }}>
                <input
                  type="text"
                  value={fd.projectNo || ""}
                  onChange={(e) => updateFormData("_flat", "projectNo", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
              <td style={{ ...sf009LblCell, width: "11%" }}>Project Name:</td>
              <td style={{ ...sf009ValCell, width: "22%" }}>
                <input
                  type="text"
                  value={fd.projectName || ""}
                  onChange={(e) => updateFormData("_flat", "projectName", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
              <td style={{ ...sf009LblCell, width: "11%" }}>Site / Location:</td>
              <td style={{ ...sf009ValCell, width: "23%" }}>
                <input
                  type="text"
                  value={fd.siteLocation || ""}
                  onChange={(e) => updateFormData("_flat", "siteLocation", e.target.value)}
                  style={sf009CellInput}
                />
              </td>
            </tr>
          </tbody>
        </table>
        <div style={{ overflowX: "auto", borderTop: sf009TblBorder }}>
          <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 1000 }}>
            <thead>
              <tr>
                <th style={{ ...sf009RegHeadCell, width: "12%" }}>Date & Time</th>
                <th style={{ ...sf009RegHeadCell, width: "36%" }}>
                  Description of Operation or Event, Present Status & Safety Status
                </th>
                <th style={{ ...sf009RegHeadCell, width: "26%" }}>Precautions & Controls</th>
                <th style={{ ...sf009RegHeadCell, width: "14%" }}>Printed Name</th>
                <th style={{ ...sf009RegHeadCell, width: "12%" }}>Signature</th>
                <th style={{ ...sf009RegHeadCell, width: 40 }}>&nbsp;</th>
              </tr>
            </thead>
            <tbody>
              {entries.map((row, idx) => {
                if (!hasLogRowData(row)) return null;
                return (
                  <tr key={idx}>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.dateTime || ""}
                        onChange={(e) => updateLogRow(idx, "dateTime", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.description || ""}
                        onChange={(e) => updateLogRow(idx, "description", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.precautionsControls || ""}
                        onChange={(e) => updateLogRow(idx, "precautionsControls", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009RegCell}>
                      <input
                        type="text"
                        value={row.printedName || ""}
                        onChange={(e) => updateLogRow(idx, "printedName", e.target.value)}
                        style={sf009CellInput}
                      />
                    </td>
                    <td style={sf009SigCell}>
                      <LogSignatureDisplay value={row.signature} />
                    </td>
                    <td style={{ ...sf009RegCell, textAlign: "center" }}>
                      <button
                        type="button"
                        onClick={() => removeLogRow(idx)}
                        title="Remove row"
                        style={{
                          background: "transparent",
                          border: "none",
                          color: "#e53e3e",
                          cursor: "pointer",
                          padding: 4,
                        }}
                      >
                        <i className="fa fa-trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        <table style={{ width: "100%", borderCollapse: "collapse", borderTop: sf009TblBorder }}>
          <tbody>
            <tr>
              <td style={sf009LegendCell}>
                {LOG_INFO_LEGEND_LINES.map((line, i) => (
                  <div key={i} style={{ marginBottom: i === 0 ? 4 : 2, fontWeight: i === 0 ? 600 : 400 }}>
                    {line}
                  </div>
                ))}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <>
      <div
        style={{
          background: "#fff",
          borderRadius: 20,
          boxShadow: "0px 4px 20px rgba(0,0,0,0.08)",
          padding: 24,
          marginBottom: 24,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: 24,
            flexWrap: "wrap",
            gap: 16,
          }}
        >
          <h2 style={{ fontSize: 24, fontWeight: 700, color: "#1a202c", margin: 0 }}>
            SF-009-I Switch Room Electrical Safety Log
          </h2>
          <div style={{ display: "flex", alignItems: "center", gap: 12, flexWrap: "wrap" }}>
            {isAdminUser && (
              <button
                type="button"
                onClick={() => setShowImportModal(true)}
                style={{
                  background: THEME,
                  color: "#fff",
                  border: "none",
                  borderRadius: 10,
                  padding: "10px 14px",
                  fontWeight: 600,
                  fontSize: 13,
                  cursor: "pointer",
                }}
              >
                Import File
              </button>
            )}
            <div style={{ position: "relative", display: "flex", alignItems: "center" }}>
              <i
                className="fa fa-search"
                style={{ position: "absolute", left: 12, color: THEME, fontSize: 14, pointerEvents: "none", zIndex: 1 }}
              />
              <input
                type="text"
                placeholder="Type to Search"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                style={{
                  padding: "10px 12px 10px 36px",
                  borderRadius: 12,
                  border: "1px solid #e2e8f0",
                  fontSize: 14,
                  outline: "none",
                  width: 250,
                  boxSizing: "border-box",
                }}
              />
            </div>
          </div>
        </div>
        <div style={{ overflowX: "auto", borderRadius: 12, border: "1px solid #e2e8f0" }}>
          <table style={{ width: "100%", borderCollapse: "collapse" }}>
            <thead>
              <tr style={{ background: "#f7fafc", borderBottom: "1px solid #e2e8f0" }}>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Project No
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Project Name
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Site / Location
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Added By
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Updated Date
                </th>
                <th style={{ fontWeight: 600, color: THEME, fontSize: 14, padding: "12px 16px", textAlign: "left" }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loadingRecords ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 40, color: THEME, fontSize: 16, fontWeight: 600 }}>
                    <i className="fa fa-spinner fa-spin" style={{ marginRight: 8 }} />
                    Loading records...
                  </td>
                </tr>
              ) : paged.length === 0 ? (
                <tr>
                  <td colSpan={6} style={{ textAlign: "center", padding: 40, color: "#718096", fontSize: 16 }}>
                    No records found.
                  </td>
                </tr>
              ) : (
                paged.map((r, idx) => {
                  const raw = mergeRecordIntoDefaults(getRawFormData(r));
                  const updatedDate = r.updatedAt || r.createdAt || r.date;
                  return (
                    <tr key={r._id || idx} style={{ borderBottom: "1px solid #f1f5f9" }}>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.projectNo || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.projectName || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{raw.siteLocation || "—"}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{getUserName(r)}</td>
                      <td style={{ color: "#2d3748", fontSize: 14, padding: 16 }}>{formatDate(updatedDate)}</td>
                      <td style={{ padding: 16 }}>
                        <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                          {canEditDelete && (
                            <button
                              onClick={() => handleEdit(r)}
                              title="Edit"
                              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                            >
                              <i className="fa fa-edit" style={{ color: "#22c55e", fontSize: 16 }} />
                            </button>
                          )}
                          <button
                            type="button"
                            onClick={() => generatePDF(r)}
                            title="Generate PDF"
                            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                          >
                            <i className="fa fa-file-pdf-o" style={{ color: "#dc2626", fontSize: 16 }} />
                          </button>
                          {(() => {
                            const k = `${r._id || ""}|${raw.projectName || ""}|${r.date || ""}`;
                            const busy = uploadingKey === k;
                            return (
                              <button
                                type="button"
                                onClick={() => {
                                  if (!busy) {
                                    generatePDF(r, { uploadOnly: true, onSuccess: () => {}, onError: () => {} });
                                  }
                                }}
                                disabled={busy}
                                title={busy ? "Uploading..." : "Upload to SharePoint"}
                                style={{
                                  background: "transparent",
                                  border: "none",
                                  cursor: busy ? "not-allowed" : "pointer",
                                  padding: 6,
                                  opacity: busy ? 0.6 : 1,
                                }}
                              >
                                <i
                                  className={busy ? "fa fa-spinner fa-spin" : "fa fa-upload"}
                                  style={{ color: "#22c55e", fontSize: 16 }}
                                />
                              </button>
                            );
                          })()}
                          {canEditDelete && (
                            <button
                              onClick={() => handleDelete(r._id)}
                              title="Delete"
                              style={{ background: "transparent", border: "none", cursor: "pointer", padding: 6 }}
                            >
                              <i className="fa fa-trash" style={{ color: "#e53e3e", fontSize: 16 }} />
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: 18, gap: 12 }}>
          <button
            className="horizon-table-btn"
            disabled={page === 1}
            onClick={() => setPage(page - 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === 1 ? "#f7fafc" : "#fff",
              color: page === 1 ? "#a0aec0" : THEME,
              cursor: page === 1 ? "not-allowed" : "pointer",
            }}
          >
            Prev
          </button>
          <span style={{ fontWeight: 700, color: THEME, fontSize: 16 }}>
            {page} / {totalPages}
          </span>
          <button
            className="horizon-table-btn"
            disabled={page === totalPages}
            onClick={() => setPage(page + 1)}
            style={{
              padding: "8px 16px",
              borderRadius: 8,
              border: "1px solid #e2e8f0",
              background: page === totalPages ? "#f7fafc" : "#fff",
              color: page === totalPages ? "#a0aec0" : THEME,
              cursor: page === totalPages ? "not-allowed" : "pointer",
            }}
          >
            Next
          </button>
        </div>
      </div>

      {showEditModal && editForm && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-xl modal-dialog-centered modal-dialog-scrollable">
            <div className="modal-content" style={{ maxWidth: 980, margin: "0 auto" }}>
              <div className="modal-header" style={{ background: THEME, color: "#fff" }}>
                <h5 className="modal-title">Edit SF-009-I Switch Room Electrical Safety Log</h5>
                <button
                  type="button"
                  className="btn-close btn-close-white"
                  onClick={() => {
                    setShowEditModal(false);
                    setLogSignatureLock(null);
                  }}
                />
              </div>
              <div className="modal-body" style={{ maxHeight: "75vh", overflowY: "auto", background: "#f8fafc", padding: 16 }}>
                {renderLogForm()}
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => {
                    setShowEditModal(false);
                    setLogSignatureLock(null);
                  }}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="btn"
                  style={{ background: THEME, color: "#fff", fontWeight: 600 }}
                  onClick={handleEditSave}
                >
                  Submit
                </button>
              </div>
            </div>
          </div>
        </div>
      )}



      <AdminTaylorImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        title="Upload SF-009-I Switch Room Electrical Safety Log File"
        importPath="sf-009-i-switch-room-electrical-safety-log/import-form"
        isAdminUser={isAdminUser}
        onImported={({ insertedRow }) => {
          if (insertedRow && insertedRow._id) {
            setForms((prev) => [insertedRow, ...prev]);
            setPage(1);
          }
        }}
      />

      {showDeleteModal && (
        <div className="modal fade show" style={{ display: "block", background: "rgba(0,0,0,0.3)" }} tabIndex="-1">
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">Delete SF-009-I Switch Room Electrical Safety Log</h5>
                <button type="button" className="btn-close" onClick={() => setShowDeleteModal(false)} />
              </div>
              <div className="modal-body">Are you sure you want to delete this form?</div>
              <div className="modal-footer">
                <button type="button" className="btn btn-secondary" onClick={() => setShowDeleteModal(false)}>
                  Cancel
                </button>
                <button type="button" className="btn btn-danger" onClick={confirmDelete}>
                  Delete
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default Sf009ISwitchRoomElectricalSafetyLogPage;
